import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLocation, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Activity, Shield, Eye, Send, UserPlus, AlertCircle, CheckCircle2, XCircle,
  Pause, Play, Calendar, Download, RefreshCw, Linkedin, Trash2, ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAppMessage } from '../../components/AppMessage';
import { fmtEST, fmtDateTimeEST } from '../../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'campaigns', label: 'Campaigns', icon: Send },
  { id: 'activity',  label: 'Activity feed', icon: Activity },
  { id: 'watchdog',  label: 'Watchdog', icon: Shield },
  { id: 'queue',     label: 'Queue', icon: Calendar },
];

const STATUS_COLORS = {
  running: 'bg-blue-100 text-blue-800 border-blue-200',
  paused: 'bg-amber-100 text-amber-800 border-amber-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  draft: 'bg-gray-100 text-gray-700 border-gray-200',
  failed: 'bg-red-100 text-red-800 border-red-200',
  queued: 'bg-gray-100 text-gray-700 border-gray-200',
};

const SEV_COLORS = {
  info: 'text-gray-600',
  warn: 'text-amber-700',
  refuse: 'text-rose-600',
  pause: 'text-red-700',
};

const ACTION_ICONS = { visit: Eye, dm: Send, invite: UserPlus };

function fmtAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return fmtEST(iso);
}

function fmtAbs(iso) {
  return fmtDateTimeEST(iso);
}

export default function LinkedinActivityPage() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const initialCampaign = params.get('campaign') || '';
  const msg = useAppMessage();

  const [tab, setTab] = useState('campaigns');
  const [campaignFilter, setCampaignFilter] = useState(initialCampaign);
  const [actionFilter, setActionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [budget, setBudget] = useState(null);
  const [watchdog, setWatchdog] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [activity, setActivity] = useState([]);
  const [wdEvents, setWdEvents] = useState([]);
  const [queue, setQueue] = useState({ buckets: [], total: 0 });

  const [loading, setLoading] = useState(false);
  const refreshRef = useRef(null);

  const loadBudgetAndWatchdog = async () => {
    try {
      const [b, w] = await Promise.all([
        axios.get(`${API}/api/linkedin-msg/budget/today`, { withCredentials: true }),
        axios.get(`${API}/api/linkedin-msg/watchdog/status`, { withCredentials: true }),
      ]);
      setBudget(b.data);
      setWatchdog(w.data);
    } catch {}
  };

  const loadCampaigns = async () => {
    const { data } = await axios.get(`${API}/api/linkedin-msg/campaigns?limit=100`, { withCredentials: true });
    setCampaigns(data.campaigns || []);
  };

  const loadActivity = async () => {
    const q = new URLSearchParams();
    if (campaignFilter) q.set('campaign_id', campaignFilter);
    if (actionFilter) q.set('action', actionFilter);
    if (statusFilter) q.set('status', statusFilter);
    q.set('limit', '200');
    const { data } = await axios.get(`${API}/api/linkedin-msg/activity?${q}`, { withCredentials: true });
    setActivity(data.activity || []);
  };

  const loadWatchdogEvents = async () => {
    const q = new URLSearchParams();
    if (campaignFilter) q.set('campaign_id', campaignFilter);
    q.set('limit', '200');
    const { data } = await axios.get(`${API}/api/linkedin-msg/watchdog/events?${q}`, { withCredentials: true });
    setWdEvents(data.events || []);
  };

  const loadQueue = async () => {
    const { data } = await axios.get(`${API}/api/linkedin-msg/queue/upcoming?days=14`, { withCredentials: true });
    setQueue(data);
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      await loadBudgetAndWatchdog();
      if (tab === 'campaigns') await loadCampaigns();
      if (tab === 'activity') await loadActivity();
      if (tab === 'watchdog') await loadWatchdogEvents();
      if (tab === 'queue') await loadQueue();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    if (refreshRef.current) clearInterval(refreshRef.current);
    refreshRef.current = setInterval(() => { if (!document.hidden) loadAll(); }, 15000);
    return () => { if (refreshRef.current) clearInterval(refreshRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, campaignFilter, actionFilter, statusFilter]);

  const campaignAction = async (id, action) => {
    try {
      await axios.post(`${API}/api/linkedin-msg/campaigns/${id}/${action}`, null, { withCredentials: true });
      msg.success(`Campaign ${action}d`);
      loadAll();
    } catch (e) {
      msg.error(`Failed to ${action} campaign`, e.response?.data?.detail || e.message, e.response?.data);
    }
  };

  const deleteCampaign = async (c) => {
    const ok = await msg.confirm(
      'Delete campaign?',
      `"${c.name}" and all its recipients + activity entries will be removed. This cannot be undone.`,
      { confirmLabel: 'Delete', destructive: true },
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/linkedin-msg/campaigns/${c.id}`, { withCredentials: true });
      msg.success('Campaign deleted');
      loadAll();
    } catch (e) {
      msg.error('Delete failed', e.response?.data?.detail || e.message, e.response?.data);
    }
  };

  const exportCsv = () => {
    let rows = [];
    let header = [];
    if (tab === 'activity') {
      header = ['timestamp', 'action', 'status', 'campaign_id', 'recipient_id', 'detail'];
      rows = activity.map((a) => [a.created_at, a.action, a.status, a.campaign_id, a.recipient_id, (a.detail || '').replace(/"/g, '""')]);
    } else if (tab === 'watchdog') {
      header = ['timestamp', 'severity', 'rule', 'detail', 'campaign_id'];
      rows = wdEvents.map((a) => [a.created_at, a.severity, a.rule, (a.detail || '').replace(/"/g, '""'), a.campaign_id]);
    } else if (tab === 'campaigns') {
      header = ['name', 'status', 'total', 'sent', 'replied', 'failed', 'skipped', 'created_at'];
      rows = campaigns.map((c) => [c.name, c.status, c.total_recipients, c.sent_count, c.replied_count, c.failed_count, c.skipped_count, c.created_at]);
    }
    const csv = [header, ...rows].map((r) => r.map((c) => `"${c ?? ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `linkedin-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-[1600px] mx-auto py-8 px-4">
      <Link
        to="/admin/marketing/linkedin-data"
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-4"
        data-testid="back-to-linkedin-data"
      >
        <ArrowLeft className="w-4 h-4" /> Back to LinkedIn Data
      </Link>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-[#0A66C2] rounded-lg p-2">
          <Linkedin className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">LinkedIn Messaging Activity</h1>
          <p className="text-sm text-gray-500">Real-time log of visits, DMs, connection requests + watchdog events</p>
        </div>
        <Button onClick={loadAll} variant="outline" size="sm" className="gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
        <Button onClick={exportCsv} variant="outline" size="sm" className="gap-2">
          <Download className="w-4 h-4" /> CSV
        </Button>
      </div>

      {/* Top strip: budget + watchdog */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        <Card className="px-4 py-3">
          <div className="text-[10px] uppercase tracking-wide text-gray-400 flex items-center gap-1">
            <Eye className="w-3 h-3" /> Profile visits today
          </div>
          <div className="flex items-baseline gap-1 mt-1">
            <span className="text-xl font-bold text-gray-900">{budget?.visits_used ?? 0}</span>
            <span className="text-sm text-gray-500">/ 300</span>
          </div>
          <div className="mt-1.5 h-1 bg-gray-100 rounded">
            <div className="h-full bg-[#0A66C2] rounded" style={{ width: `${Math.min(100, ((budget?.visits_used ?? 0) / 300) * 100)}%` }} />
          </div>
        </Card>
        <Card className="px-4 py-3">
          <div className="text-[10px] uppercase tracking-wide text-gray-400 flex items-center gap-1">
            <Send className="w-3 h-3" /> DMs today
          </div>
          <div className="flex items-baseline gap-1 mt-1">
            <span className="text-xl font-bold text-gray-900">{budget?.dms_used ?? 0}</span>
            <span className="text-sm text-gray-500">/ 50</span>
          </div>
          <div className="mt-1.5 h-1 bg-gray-100 rounded">
            <div className="h-full bg-blue-500 rounded" style={{ width: `${Math.min(100, ((budget?.dms_used ?? 0) / 50) * 100)}%` }} />
          </div>
        </Card>
        <Card className="px-4 py-3">
          <div className="text-[10px] uppercase tracking-wide text-gray-400 flex items-center gap-1">
            <UserPlus className="w-3 h-3" /> Invites today
          </div>
          <div className="flex items-baseline gap-1 mt-1">
            <span className="text-xl font-bold text-gray-900">{budget?.invites_used ?? 0}</span>
            <span className="text-sm text-gray-500">/ 100 wk</span>
          </div>
          <div className="mt-1.5 h-1 bg-gray-100 rounded">
            <div className="h-full bg-amber-500 rounded" style={{ width: `${Math.min(100, ((budget?.invites_used ?? 0) / 100) * 100)}%` }} />
          </div>
        </Card>
        <Card className="px-4 py-3 border-emerald-200 bg-emerald-50/30">
          <div className="text-[10px] uppercase tracking-wide text-emerald-700 flex items-center gap-1">
            <Shield className="w-3 h-3" /> Watchdog trust
          </div>
          <div className="flex items-baseline gap-1 mt-1">
            <span className="text-xl font-bold text-emerald-800">{watchdog?.trust_score ?? 0}</span>
            <span className="text-sm text-emerald-700">/ 100</span>
          </div>
          <div className="text-[11px] text-emerald-700 mt-0.5">
            Risk {watchdog?.risk_score ?? 0} · {budget?.challenges_hit ?? 0} challenges
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 mb-4">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${active ? 'border-[#0A66C2] text-[#0A66C2]' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Filter bar */}
      {(tab === 'activity' || tab === 'watchdog') && (
        <div className="flex items-center gap-2 mb-3 text-xs">
          <select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)}
            className="h-8 px-2 border border-gray-200 rounded text-xs">
            <option value="">All campaigns</option>
            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {tab === 'activity' && (
            <>
              <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}
                className="h-8 px-2 border border-gray-200 rounded text-xs">
                <option value="">All actions</option>
                <option value="visit">Visit</option>
                <option value="dm">DM</option>
                <option value="invite">Invite</option>
              </select>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="h-8 px-2 border border-gray-200 rounded text-xs">
                <option value="">All status</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
                <option value="dry_run">Dry-run</option>
              </select>
            </>
          )}
        </div>
      )}

      {/* Tab content */}
      {tab === 'campaigns' && (
        <Card>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr className="text-gray-600">
                <th className="text-left py-2.5 px-4 font-medium">Name</th>
                <th className="text-left py-2.5 px-4 font-medium">Status</th>
                <th className="text-left py-2.5 px-4 font-medium">Progress</th>
                <th className="text-left py-2.5 px-4 font-medium" title="Done = visits + DMs + invites">Done / Replied / Failed</th>
                <th className="text-left py-2.5 px-4 font-medium">Created</th>
                <th className="text-right py-2.5 px-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-gray-400 py-8">No campaigns yet</td></tr>
              ) : campaigns.map((c) => {
                const pct = c.total_recipients > 0 ? Math.round((c.sent_count / c.total_recipients) * 100) : 0;
                return (
                  <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-2.5 px-4 text-gray-900 font-medium">{c.name}</td>
                    <td className="py-2.5 px-4">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border ${STATUS_COLORS[c.status] || STATUS_COLORS.queued}`}>
                        {c.status}
                      </span>
                      {c.live && c.status === 'running' && <span className="ml-2 text-[10px] text-rose-700 font-semibold">LIVE</span>}
                    </td>
                    <td className="py-2.5 px-4 w-64">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded">
                          <div className="h-full bg-[#0A66C2] rounded" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-gray-500">{c.sent_count}/{c.total_recipients}</span>
                      </div>
                    </td>
                    <td className="py-2.5 px-4 text-xs text-gray-600">
                      {c.sent_count} / <span className="text-green-700">{c.replied_count}</span> / <span className="text-red-700">{c.failed_count}</span>
                    </td>
                    <td className="py-2.5 px-4 text-xs font-mono text-gray-500">{fmtAbs(c.created_at)}</td>
                    <td className="py-2.5 px-4 text-right">
                      <div className="flex gap-1 justify-end">
                        {c.status === 'running' && (
                          <button onClick={() => campaignAction(c.id, 'pause')}
                            className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-200 hover:bg-gray-50">
                            <Pause className="w-3 h-3" /> Pause
                          </button>
                        )}
                        {c.status === 'paused' && (
                          <button onClick={() => campaignAction(c.id, 'resume')}
                            className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-200 hover:bg-gray-50">
                            <Play className="w-3 h-3" /> Resume
                          </button>
                        )}
                        <button onClick={() => { setTab('activity'); setCampaignFilter(c.id); }}
                          className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-200 hover:bg-gray-50">
                          <Activity className="w-3 h-3" /> Activity
                        </button>
                        <button onClick={() => deleteCampaign(c)}
                          className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100">
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {tab === 'activity' && (
        <Card className="divide-y divide-gray-100">
          {activity.length === 0 ? (
            <div className="text-center text-gray-400 py-8">No activity</div>
          ) : activity.map((a) => {
            const Icon = ACTION_ICONS[a.action] || Activity;
            const statusOk = a.status === 'sent';
            const statusDry = a.status === 'dry_run';
            const StatusIcon = statusOk ? CheckCircle2 : statusDry ? Eye : XCircle;
            // Context-aware label: "visit" action → "Visited"; "dm" → "Sent"; "invite" → "Invited"
            const successLabel = {
              visit: 'Visited',
              dm: 'Sent',
              invite: 'Invite sent',
            }[a.action] || 'Done';
            const dryLabel = {
              visit: 'Dry-run visit',
              dm: 'Dry-run DM',
              invite: 'Dry-run invite',
            }[a.action] || 'Dry run';
            const statusLabel = statusOk ? successLabel : statusDry ? dryLabel : a.status.replace('_', ' ');
            return (
              <div key={a._id || `${a.created_at}-${a.recipient_id}`} className="flex items-center gap-3 px-4 py-2.5">
                <div className="text-[10px] text-gray-500 font-mono w-40 whitespace-nowrap">{fmtAbs(a.created_at)}</div>
                <Icon className="w-4 h-4 text-gray-500 shrink-0" />
                <span className="text-xs font-medium text-gray-700 w-14 capitalize">{a.action}</span>
                <StatusIcon className={`w-4 h-4 shrink-0 ${statusOk ? 'text-emerald-600' : statusDry ? 'text-gray-500' : 'text-rose-500'}`} />
                <span className={`text-xs w-24 capitalize ${statusOk ? 'text-emerald-700' : statusDry ? 'text-gray-600' : 'text-rose-700'}`}>
                  {statusLabel}
                </span>
                <span className="text-xs text-gray-600 truncate flex-1" title={a.detail}>{a.detail}</span>
              </div>
            );
          })}
        </Card>
      )}

      {tab === 'watchdog' && (
        <Card className="divide-y divide-gray-100">
          {wdEvents.length === 0 ? (
            <div className="text-center text-gray-400 py-8">No watchdog events</div>
          ) : wdEvents.map((e) => (
            <div key={e._id || `${e.created_at}-${e.rule}`} className="flex items-center gap-3 px-4 py-2.5">
              <div className="text-[10px] text-gray-500 font-mono w-40 whitespace-nowrap">{fmtAbs(e.created_at)}</div>
              <span className={`text-[11px] font-semibold uppercase w-16 ${SEV_COLORS[e.severity] || 'text-gray-500'}`}>{e.severity}</span>
              <span className="text-xs font-medium text-gray-700 w-44 truncate">{e.rule}</span>
              <span className="text-xs text-gray-600 truncate flex-1">{e.detail}</span>
            </div>
          ))}
        </Card>
      )}

      {tab === 'queue' && (
        <Card className="p-6">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Upcoming recipients · next 14 days</h3>
          {queue.buckets.length === 0 ? (
            <p className="text-center text-gray-400 py-8">Nothing queued</p>
          ) : (
            <div className="grid grid-cols-14 gap-2 items-end" style={{ gridTemplateColumns: `repeat(${queue.buckets.length}, 1fr)` }}>
              {queue.buckets.map((b) => {
                const max = Math.max(...queue.buckets.map((x) => x.count));
                const h = max > 0 ? (b.count / max) * 100 : 0;
                return (
                  <div key={b.date} className="flex flex-col items-center gap-1">
                    <div className="w-full h-32 flex items-end">
                      <div className="w-full bg-[#0A66C2] rounded-t" style={{ height: `${h}%` }} />
                    </div>
                    <span className="text-[10px] text-gray-500">{new Date(b.date).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>
                    <span className="text-xs font-semibold text-gray-900">{b.count}</span>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-xs text-gray-500 mt-4">Total queued: <strong>{queue.total}</strong></p>
        </Card>
      )}
    </div>
  );
}
